import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import {useState} from "react";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/tournament-formats')({
    component: RouteComponent,
})

// Several formats don't have a dedicated generator page yet, so each CTA points at the
// closest existing tool: round robin -> its own page, brackets -> create-brackets,
// social rotating formats -> americano-mixer.
const RR = "/round-robin-tournament-generator/";
const SE = "/single-elimination-bracket-generator/";
const DE = "/double-elimination-bracket-generator/";
const MEX = "/mexicano-tournament-generator/";
const SWISS = "/swiss-system-tournament-generator/";
const LADDER = "/ladder-league-generator/";
const KING = "/king-of-the-court-generator/";
const GROUP = "/group-and-knockout-generator/";
const AMERICANO = "/americano-generator/";

type Format = {code: string; name: string; blurb: string; cta: string; to: string};

const FORMATS: Format[] = [
    {code: "RR", name: "Round Robin", blurb: "Every participant plays every other participant. Great for fair rankings and leagues.", cta: "Generate fixtures", to: RR},
    {code: "SE", name: "Single Elimination", blurb: "Lose once and you're out. Fast, simple and ideal for larger tournaments.", cta: "Generate bracket", to: SE},
    {code: "DE", name: "Double Elimination", blurb: "Players get a second chance through a winners and losers bracket.", cta: "Generate bracket", to: DE},
    {code: "A", name: "Americano", blurb: "A popular rotating doubles format for padel, tennis and pickleball club events.", cta: "Generate Americano", to: AMERICANO},
    {code: "M", name: "Mexicano", blurb: "A dynamic social format where pairings adapt based on player results.", cta: "Generate Mexicano", to: MEX},
    {code: "S", name: "Swiss System", blurb: "Players face opponents with similar records without everyone playing everyone.", cta: "Generate Swiss draw", to: SWISS},
    {code: "L", name: "Ladder League", blurb: "Players move up or down rankings over time based on results.", cta: "Create ladder", to: LADDER},
    {code: "K", name: "King of the Court", blurb: "A fast-paced format where winners move up and challengers rotate in.", cta: "Plan format", to: KING},
    {code: "G", name: "Group + Knockout", blurb: "Start with groups, then move top players or teams into knockout rounds.", cta: "Generate tournament", to: GROUP},
];

type Compare = {name: string; fairness: number; volume: string; bestFor: string; to: string};

const COMPARE: Compare[] = [
    {name: "Round Robin", fairness: 5, volume: "High", bestFor: "Leagues, rankings, fair competition", to: RR},
    {name: "Single Elimination", fairness: 3, volume: "Low", bestFor: "Large events, quick tournaments", to: SE},
    {name: "Double Elimination", fairness: 4, volume: "Medium", bestFor: "Competitive tournaments", to: DE},
    {name: "Americano", fairness: 4, volume: "Medium", bestFor: "Padel, tennis, pickleball club nights", to: AMERICANO},
    {name: "Mexicano", fairness: 5, volume: "Medium", bestFor: "Social events with balanced matches", to: MEX},
    {name: "Swiss System", fairness: 4, volume: "Medium", bestFor: "Large tournaments with limited rounds", to: SWISS},
];

type Guide = {tag: string; name: string; desc: string; ctaLabel: string; to: string; steps: string[]; bestFor: string; pros: string[]; cons: string[]};

const GUIDES: Guide[] = [
    {
        tag: "Most fair", name: "Round Robin Tournament", to: RR, ctaLabel: "Generate Round Robin",
        desc: "In a round robin tournament, every player or team plays every other participant. This creates fair standings and gives everyone multiple matches.",
        steps: [
            "All participants are entered into one group.",
            "Fixtures are generated so each participant plays everyone else.",
            "Results update the standings after every match.",
            "The participant with the best ranking wins, or top players qualify for playoffs.",
        ],
        bestFor: "Club leagues, small tournaments, padel boxes, tennis groups and events where everyone should play multiple matches.",
        pros: ["Very fair rankings", "More matches for every player", "Great for leagues and clubs"],
        cons: ["Can take longer", "Needs more courts or time slots", "Less suitable for huge events"],
    },
    {
        tag: "Fastest", name: "Single Elimination Tournament", to: SE, ctaLabel: "Generate Bracket",
        desc: "In single elimination, players are removed from the tournament after one loss. It is simple, dramatic and quick to complete.",
        steps: [
            "Participants are seeded or randomly placed into a bracket.",
            "Each round eliminates the losing players or teams.",
            "Winners progress to quarter-finals, semi-finals and finals.",
            "The final winner becomes champion.",
        ],
        bestFor: "One-day tournaments, cup competitions, finals days and events where time or court availability is limited.",
        pros: ["Fast to run", "Easy to understand", "Works for large events"],
        cons: ["One bad match can eliminate a strong player", "Less playing time", "Less accurate rankings"],
    },
    {
        tag: "Second chance", name: "Double Elimination Tournament", to: DE, ctaLabel: "Generate Double Elimination",
        desc: "Double elimination gives each participant two chances. Players continue in the losers bracket after their first defeat.",
        steps: [
            "Everyone starts in the winners bracket.",
            "A first loss moves the participant into the losers bracket.",
            "A second loss eliminates them from the tournament.",
            "The winners and losers bracket champions meet in the final.",
        ],
        bestFor: "Competitive events where organisers want knockout drama without eliminating strong players after one bad match.",
        pros: ["Fairer than single elimination", "More competitive structure", "Good for serious events"],
        cons: ["More complex bracket", "Takes longer", "Can be harder to explain"],
    },
    {
        tag: "Club favourite", name: "Americano Tournament", to: AMERICANO, ctaLabel: "Generate Americano",
        desc: "Americano is a rotating doubles format commonly used in padel, tennis and pickleball clubs. Players rotate partners and opponents throughout the event.",
        steps: [
            "Players are added as individuals rather than fixed teams.",
            "Each round creates new pairings and opponents.",
            "Players earn points from each match regardless of partner.",
            "The player with the highest individual score wins.",
        ],
        bestFor: "Padel clubs, pickleball socials, tennis mixers and member events where players want to play with different partners.",
        pros: ["Great for social play", "Players meet different partners", "Ideal for club nights"],
        cons: ["Needs balanced player numbers", "Less familiar outside racquet sports", "Scoring rules need clear explanation"],
    },
    {
        tag: "Most balanced social", name: "Mexicano Tournament", to: MEX, ctaLabel: "Generate Mexicano",
        desc: "Mexicano is a dynamic social format where pairings adapt each round based on current results, so similarly performing players are matched for balanced games.",
        steps: [
            "Players join as individuals and play an opening round.",
            "Standings update after every round.",
            "The system re-pairs players by current ranking each round.",
            "The highest individual points total wins.",
        ],
        bestFor: "Social padel, tennis and pickleball nights that want balanced, competitive matchups throughout the event.",
        pros: ["Balanced matchups every round", "Highly social", "Keeps games competitive"],
        cons: ["Pairings change constantly", "Needs live scoring to re-pair", "Best with even player counts"],
    },
    {
        tag: "Large events", name: "Swiss System Tournament", to: SWISS, ctaLabel: "Generate Swiss Draw",
        desc: "Swiss system tournaments pair participants with others on similar records. Nobody is eliminated early, and standings improve as players face opponents with comparable performance.",
        steps: [
            "Players are paired for round one, often by seed or at random.",
            "After each round, players with similar records are matched together.",
            "Standings are calculated from wins, points and tie-breakers.",
            "After a fixed number of rounds, top players qualify or a winner is declared.",
        ],
        bestFor: "Large tournaments, racket sports and esports that need fair rankings without every player facing everyone.",
        pros: ["No early elimination", "Fair pairings for big fields", "Fixed number of rounds"],
        cons: ["Needs solid pairing logic", "Less familiar to casual players", "Tie-breakers can be complex"],
    },
    {
        tag: "Balanced", name: "Group Stage plus Playoffs", to: GROUP, ctaLabel: "Generate Tournament",
        desc: "A group stage plus playoffs format combines round robin groups with knockout finals. Players get multiple group matches before the best advance to the bracket stage.",
        steps: [
            "Participants are divided into groups.",
            "Each group runs a round robin schedule.",
            "Top participants qualify for the knockout rounds.",
            "The playoff bracket decides the overall champion.",
        ],
        bestFor: "Club championships, school tournaments and corporate sports days that need both fairness and a finals experience.",
        pros: ["Guaranteed group matches", "Exciting knockout finish", "Balances fairness and speed"],
        cons: ["More planning required", "Needs enough courts", "Longer than a single format"],
    },
    {
        tag: "Ongoing", name: "Ladder League", to: LADDER, ctaLabel: "Create Ladder",
        desc: "A ladder league is an ongoing competition where players move up or down a ranking ladder based on results, challenges or scheduled fixtures.",
        steps: [
            "Players start on a ranked ladder.",
            "Players challenge others or play scheduled matches.",
            "Results move players up or down the ladder.",
            "The ladder updates throughout the season.",
        ],
        bestFor: "Racquet clubs, box leagues and long-running internal competitions built around rankings.",
        pros: ["Great for ongoing engagement", "Flexible scheduling", "Clear rankings"],
        cons: ["Needs consistent participation", "No single finish line", "Requires challenge rules"],
    },
];

const FAQS = [
    {
        q: 'What is the fairest tournament format?',
        a: <>Round robin is the fairest format because every player or team plays every other participant, so final standings reflect overall performance rather than a single result. Mexicano is also highly fair for social events because it continually balances matchups.</>,
    },
    {
        q: 'What is the fastest tournament format?',
        a: <>Single elimination is the fastest format — each loss removes a participant, so the field halves every round. It is ideal for large events that need to finish quickly.</>,
    },
    {
        q: 'Which tournament format is best for a club night?',
        a: <>Americano and Mexicano are best for club nights. Both rotate partners and opponents so everyone plays plenty of matches and meets different people, which suits social padel, tennis and pickleball events.</>,
    },
    {
        q: 'What is the difference between Americano and Mexicano?',
        a: <>In Americano, pairings and opponents rotate on a fixed schedule. In Mexicano, the pairings adapt each round based on current results, so similarly performing players face each other for more balanced, competitive matches.</>,
    },
    {
        q: 'Can ScorePal generate fixtures for these formats?',
        a: <>Yes. ScorePal generates fixtures and brackets, manages registrations, tracks standings and publishes live scores for round robin, knockout, Americano and more — all from one platform.</>,
    },
];

function Stars({count}: {count: number}) {
    return (
        <span className="text-[#f5a524] whitespace-nowrap tracking-wide" aria-label={`${count} out of 5`}>
            {"★".repeat(count)}<span className="text-[#1a1a2e]/20">{"★".repeat(5 - count)}</span>
        </span>
    );
}

function RouteComponent() {
    const [eventType, setEventType] = useState("league");
    const [participants, setParticipants] = useState("small");

    const recommend = (): {format: string; reason: string} => {
        if (eventType === "club") {
            return {format: "Americano or Mexicano", reason: "Best for social club nights where players rotate partners and opponents."};
        }
        if (eventType === "championship") {
            return participants === "large"
                ? {format: "Single Elimination", reason: "Best for finishing a large event quickly."}
                : {format: "Double Elimination", reason: "Best when you want a competitive bracket with a second chance."};
        }
        if (eventType === "large") {
            return {format: "Swiss System", reason: "Best for large events where players need fair matchups without playing everyone."};
        }
        // league
        return participants === "large"
            ? {format: "Ladder League", reason: "Best for ongoing rankings with many players."}
            : {format: "Round Robin", reason: "Best for fair standings where everyone can play each other."};
    };

    const rec = recommend();

    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Tournament Formats Explained: Round Robin, Knockout, Americano & More | ScorePal</title>
            <meta name="description" content="Compare the most popular tournament formats including Round Robin, Single Elimination, Double Elimination, Americano, Mexicano, Swiss System and Ladder Leagues. Learn which format is best and generate fixtures instantly." />
            <meta name="keywords" content="tournament formats, tournament structure, round robin tournament, knockout tournament, single elimination, double elimination, americano tournament, mexicano tournament, swiss system tournament" />
            <link rel="canonical" href={`${config.publicUrl}/tournament-formats/`} />
            <meta property="og:title" content="Tournament Formats Explained | ScorePal" />
            <meta property="og:description" content="Compare Round Robin, Knockout, Americano, Mexicano and Swiss tournament formats." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/tournament-formats/`} />
            <meta property="og:image" content="https://scorepal.co/images/tournament-formats-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Tournament Formats Explained | ScorePal" />
            <meta name="twitter:description" content="Compare the most popular tournament formats and generate fixtures instantly." />

            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is the fairest tournament format?", "acceptedAnswer": {"@type": "Answer", "text": "Round robin is the fairest format because every player or team plays every other participant. Mexicano is also highly fair for social events because it balances matchups each round."}},
                    {"@type": "Question", "name": "What is the fastest tournament format?", "acceptedAnswer": {"@type": "Answer", "text": "Single elimination is the fastest format; each loss removes a participant, so the field halves every round."}},
                    {"@type": "Question", "name": "Which tournament format is best for a club night?", "acceptedAnswer": {"@type": "Answer", "text": "Americano and Mexicano are best for club nights because they rotate partners and opponents so everyone plays plenty of matches."}},
                    {"@type": "Question", "name": "What is the difference between Americano and Mexicano?", "acceptedAnswer": {"@type": "Answer", "text": "Americano rotates pairings on a fixed schedule, while Mexicano adapts pairings each round based on current results for more balanced matches."}},
                    {"@type": "Question", "name": "Can ScorePal generate fixtures for these formats?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal generates fixtures and brackets, manages registrations, tracks standings and publishes live scores for round robin, knockout, Americano and more."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Tournament Formats", item: "https://scorepal.co/tournament-formats/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Tournament Planning Guide
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(40px,6vw,72px)] leading-none font-bold tracking-wide mb-5">
                                    Tournament Formats Explained
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    Compare the most popular tournament formats, choose the best structure for your event and
                                    generate fixtures for round robin, knockout, Americano, Mexicano, Swiss system and ladder leagues.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <a href="#formats" className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Explore Formats</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </a>
                                    <a href="#selector" className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Find Best Format
                                    </a>
                                </div>
                            </div>

                            {/* "At a glance" card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Popular formats at a glance</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">
                                    Different events need different structures — some maximise fairness, some speed, others suit social club nights.
                                </p>
                                <div className="space-y-3">
                                    {[
                                        {label: "Most fair", value: "Round Robin"},
                                        {label: "Fastest", value: "Single Elimination"},
                                        {label: "Best for clubs", value: "Americano"},
                                        {label: "Best for rankings", value: "Swiss / Ladder"},
                                    ].map((row) => (
                                        <div key={row.label} className="flex items-center justify-between rounded-xl bg-white/10 px-4 py-3">
                                            <strong className="font-inter text-white text-sm">{row.label}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What is a tournament format? */}
            <section className="py-12 lg:py-16 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="rounded-3xl bg-white p-8 lg:p-10 shadow-sm max-w-4xl mx-auto">
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(26px,3.5vw,38px)] leading-tight tracking-wide mb-4">
                            What is a tournament format?
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/80 leading-relaxed mb-6">
                            A tournament format is the structure that determines who plays who, how winners progress, how
                            standings are calculated and how a champion is decided. The right choice depends on your event
                            size, available time, fairness and the experience you want players to have.
                        </p>
                        <ul className="space-y-3">
                            {[
                                "Use round robin when fairness matters most.",
                                "Use knockout when time is limited.",
                                "Use Swiss system for larger events with many players.",
                                "Use Americano or Mexicano for social padel, tennis and pickleball nights.",
                                "Use ladder leagues for ongoing club competitions.",
                            ].map((item) => (
                                <li key={item} className="flex items-start gap-3 font-inter text-[#1a1a2e]">
                                    <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#082F8A]/10 text-[#082F8A] font-bold text-sm">✓</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Formats grid */}
            <section id="formats" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Popular Tournament Formats
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Browse the most common tournament structures and generate fixtures for the format that suits your players, courts and schedule.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FORMATS.map((f) => (
                            <Link
                                key={f.name}
                                to={f.to as any}
                                className="group bg-white rounded-2xl p-6 shadow-sm border border-transparent hover:border-[#082F8A]/25 hover:-translate-y-1 transition flex flex-col justify-between min-h-[210px]"
                            >
                                <div>
                                    <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                    <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.name}</h3>
                                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.blurb}</p>
                                </div>
                                <span className="font-inter text-[#082F8A] font-bold text-sm mt-4 group-hover:underline">{f.cta} →</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Compare table */}
            <section id="compare" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Compare Tournament Formats
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Quickly compare fairness, match volume and the best use case for each tournament format.
                    </p>

                    <div className="overflow-x-auto">
                        <div className="min-w-[760px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-[1.2fr_0.8fr_0.8fr_1.6fr_0.8fr] bg-[#F6F7FB] font-bebas text-lg text-[#1a1a2e]">
                                <div className="p-4 border-r border-[#d9dce7]">Format</div>
                                <div className="p-4 border-r border-[#d9dce7]">Fairness</div>
                                <div className="p-4 border-r border-[#d9dce7]">Match Volume</div>
                                <div className="p-4 border-r border-[#d9dce7]">Best For</div>
                                <div className="p-4">Generator</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.name} className={`grid grid-cols-[1.2fr_0.8fr_0.8fr_1.6fr_0.8fr] border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-4 border-r border-[#d9dce7] font-semibold">{row.name}</div>
                                    <div className="p-4 border-r border-[#d9dce7]"><Stars count={row.fairness} /></div>
                                    <div className="p-4 border-r border-[#d9dce7]">{row.volume}</div>
                                    <div className="p-4 border-r border-[#d9dce7] text-[#1a1a2e]/80">{row.bestFor}</div>
                                    <div className="p-4">
                                        <Link to={row.to as any} className="font-semibold text-[#082F8A] hover:underline">Open →</Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Format guides */}
            <section className="py-16 lg:py-24 bg-[#EDEEF4]">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Tournament Format Guides
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Learn when to use each format and the advantages and disadvantages of each structure.
                    </p>

                    <div className="space-y-6">
                        {GUIDES.map((g) => (
                            <article key={g.name} className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8">
                                <div>
                                    <span className="inline-flex rounded-full bg-[#EDEEF4] text-[#082F8A] px-3 py-1 text-xs font-bold mb-3">{g.tag}</span>
                                    <h3 className="font-inter text-[#1a1a2e] font-semibold text-xl mb-2">{g.name}</h3>
                                    <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed mb-4">{g.desc}</p>

                                    <ol className="space-y-2.5 mb-4">
                                        {g.steps.map((s, i) => (
                                            <li key={i} className="flex items-start gap-3 font-inter text-[#1a1a2e]/80 text-sm leading-relaxed">
                                                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#082F8A] text-white font-bold text-xs">{i + 1}</span>
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ol>

                                    <p className="font-inter text-[#1a1a2e]/80 text-sm leading-relaxed mb-5">
                                        <strong className="text-[#1a1a2e]">Best for:</strong> {g.bestFor}
                                    </p>

                                    <Link to={g.to as any} className="inline-flex items-center gap-2 bg-[#082F8A] text-white rounded-full pl-5 pr-1 py-1 font-semibold hover:bg-[#06246b] transition">
                                        <span className="font-inter text-sm font-medium">{g.ctaLabel}</span>
                                        <div className="bg-white rounded-full p-1">
                                            <ArrowUpRightIcon className="text-[#082F8A]" size={24} />
                                        </div>
                                    </Link>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-[#F8FAFC] border border-[#e5e7eb] rounded-2xl p-5">
                                        <h4 className="font-inter font-semibold text-[#12b76a] mb-2">Advantages</h4>
                                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-[#1a1a2e]/80">
                                            {g.pros.map((p) => <li key={p}>{p}</li>)}
                                        </ul>
                                    </div>
                                    <div className="bg-[#F8FAFC] border border-[#e5e7eb] rounded-2xl p-5">
                                        <h4 className="font-inter font-semibold text-[#d92d20] mb-2">Disadvantages</h4>
                                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-[#1a1a2e]/80">
                                            {g.cons.map((c) => <li key={c}>{c}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Format selector */}
            <section id="selector" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div>
                            <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-4">Format Selector</span>
                            <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-3">
                                Which tournament format should you use?
                            </h2>
                            <p className="font-inter text-white/75 text-base leading-relaxed">
                                Answer two quick questions and get a recommended tournament format for your event.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-6 lg:p-8">
                            <label htmlFor="eventType" className="block font-inter font-semibold text-[#1a1a2e] mb-2">What are you organizing?</label>
                            <select
                                id="eventType"
                                value={eventType}
                                onChange={(e) => setEventType(e.target.value)}
                                className="w-full rounded-xl border border-[#e5e7eb] p-3.5 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                            >
                                <option value="league">League or ranking event</option>
                                <option value="club">Club night or social event</option>
                                <option value="championship">Championship tournament</option>
                                <option value="large">Large event with many players</option>
                            </select>

                            <label htmlFor="participants" className="block font-inter font-semibold text-[#1a1a2e] mt-4 mb-2">How many participants?</label>
                            <select
                                id="participants"
                                value={participants}
                                onChange={(e) => setParticipants(e.target.value)}
                                className="w-full rounded-xl border border-[#e5e7eb] p-3.5 font-inter text-[#1a1a2e] focus:outline-none focus:ring-2 focus:ring-[#082F8A]/40"
                            >
                                <option value="small">Under 12</option>
                                <option value="medium">12 to 32</option>
                                <option value="large">32+</option>
                            </select>

                            <div className="mt-5 rounded-2xl bg-[#EDEEF4] border border-[#082F8A]/15 p-5">
                                <span className="font-inter text-sm text-[#1a1a2e]/70">Recommended format:</span>
                                <strong className="block font-bebas text-2xl text-[#082F8A] tracking-wide mt-1">{rec.format}</strong>
                                <p className="font-inter text-sm text-[#1a1a2e]/80 mt-2">{rec.reason}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run Any Tournament With ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Generate fixtures, manage registrations, track standings, collect scores and publish live results for
                        clubs, leagues and tournament organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Round Robin", "Americano", "Mexicano", "Knockout", "Swiss System", "Live Scores"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Tournament Format FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
